import { ResultRecord } from "@rsm-hcd/javascript-core";
import PageEditSessionRecord from "models/view-models/page-edit-session-record";
import { useCallback } from "react";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import { PageEditSessionService } from "utilities/services/notebooks/page-edit-session-service";
const PAGE_EDIT_SESSION_CONFLICT =
    "PageEditSessionsConductor_PageEditSessionConflict";
const usePageEditSession = (editModeEnabled: boolean) => {
    // const { get: getCurrentSession } = PageEditSessionService.useGetQuery();
    const { create: createSession } = PageEditSessionService.useCreate();
    const { get: getActiveSession } =
        PageEditSessionService.useGetActiveSessionQuery();
    const { currentPage } = useCurrentPage();
    const { delete: endSession } = PageEditSessionService.useDelete();
    const POLLING_INTERVAL = 5000; // 5 seconds

    const shouldPoll = editModeEnabled && !!currentPage?.id;

    const {
        data: activeSession,
        refetch: refetchActiveSession,
        remove: removeActiveSessionFromState,
    } = getActiveSession(
        undefined,
        {
            pageId: currentPage?.id!,
        },
        {
            enabled: editModeEnabled && !!currentPage?.id,
            refetchInterval: shouldPoll ? POLLING_INTERVAL : false,
        }
    );

    const enableEditSession = useCallback(
        async (blockId?: number) => {
            if (!currentPage || !currentPage.id) {
                return false;
            }

            try {
                const newSession = new PageEditSessionRecord({
                    pageId: currentPage?.id,
                    blockId: blockId,
                });
                await createSession(newSession, {
                    pageId: currentPage?.id!,
                    blockId: blockId ?? undefined,
                });

                return true;
            } catch (error) {
                if (
                    error instanceof ResultRecord &&
                    error.hasErrorFor(PAGE_EDIT_SESSION_CONFLICT)
                ) {
                    return true;
                }
                return false;
            }
        },
        [createSession, currentPage]
    );

    const endEditSession = async () => {
        if (!currentPage || !currentPage.id) {
            return false;
        }
        // Check if there's an existing active session for the page. If not, just return true and no need to delete anything
        const existingSession = await refetchActiveSession();
        if (!existingSession.data?.resultObject) {
            return true;
        }

        try {
            if (!existingSession.data?.resultObject.id) {
                throw new Error("missing id");
            }
            // If there is an active session, delete it
            await endSession(existingSession.data.resultObject.id, {
                pageId: currentPage?.id!,
            });
            removeActiveSessionFromState();
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    return {
        activeSession: activeSession?.resultObject,
        enableEditSession,
        endEditSession,
    };
};
export default usePageEditSession;
